import React from 'react'

/**
 * Props interface for the Badge component
 */
export interface BadgeProps {
  /** Badge content */
  children: React.ReactNode
  /** Visual variant of the badge */
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'success'
    | 'warning'
    | 'error'
  /** Size of the badge */
  size?: 'xs' | 'sm' | 'md'
  /** Additional CSS classes */
  className?: string
  /** Optional title for accessibility */
  title?: string
}

/**
 * Badge component for displaying small status indicators, categories, or labels
 *
 * Features:
 * - Multiple visual variants (primary, secondary, outline, etc.)
 * - Three size options (xs, sm, md)
 * - Accessible with proper ARIA attributes
 * - Consistent with design system tokens
 * - Support for custom styling via className
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'primary',
  size = 'sm',
  className = '',
  title,
  ...props
}) => {
  const badgeClasses = [
    'badge',
    `badge--${variant}`,
    `badge--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span
      className={badgeClasses}
      title={title}
      role="status"
      aria-label={typeof children === 'string' ? children : undefined}
      {...props}
    >
      {children}
    </span>
  )
}

export default Badge
